<template>
  <div class="utleier">
    <div class="mt-3 text-center">
      <div class="small-container">
        <div class="sms-verification bg-light p-5 text-center">
          <div>
            <div v-if="SMSSent">
              <div v-if="isVerificationSuccessful || isVerificationFailed">
                <div v-if="isVerificationSuccessful">
                  <i class="budicon-solid budicon-solid-tick-sign sms-icon text-primary"></i>
                  <h2 class="mt-4 pt-2">Verifisert</h2>
                </div>

                <div v-if="isVerificationFailed">
                  <i class="budicon-solid budicon-solid-cross-sign sms-icon text-danger"></i>
                  <h2 class="mt-4 pt-2">Feil engangskode</h2>
                </div>
              </div>

              <div v-else>
                <i class="budicon-sms sms-icon text-primary"></i>
                <h2 class="mt-4 pt-2">Bekreftelse</h2>
              </div>

              <div v-if="isVerificationFailed || isVerificationSuccessful">
                  <div v-if="isVerificationSuccessful" data-cy="state-verification-successful">
                      <p class="mt-4">Ditt telefonnummer er nå verifisert.</p>
                      <button class="btn btn-primary mt-5" @click="$router.push({name: 'choose'})" data-cy="verification-successful-next">neste</button>
                  </div>

                <p
                  v-if="isVerificationFailed"
                  class="mt-4"
                  data-cy="state-verification-failed"
                >Engangskoden du skrev inn var feil. Forsøk på nytt, eventuelt send SMS på nytt.</p>
              </div>

              <p v-else class="mt-4">
                Skriv inn koden som ble sendt som SMS til
                <strong>{{ signup.user.cellPhone }}</strong>
              </p>

              <div v-if="!isVerificationSuccessful" class="sms-code-inputs" data-cy="sms-code-inputs">
                <input
                  @keyup="focusNextInput(0)"
                  @focus="resetInputValues"
                  id="sms-code-input-0"
                  type="text"
                  maxlength="1"
                  v-model="SMSCodeInputs[0]"
                  placeholder="-"
                />
                <input
                  @keyup="focusNextInput(1)"
                  @focus="resetInputValues"
                  id="sms-code-input-1"
                  type="text"
                  maxlength="1"
                  v-model="SMSCodeInputs[1]"
                  placeholder="-"
                />
                <input
                  @keyup="focusNextInput(2)"
                  @focus="resetInputValues"
                  id="sms-code-input-2"
                  type="text"
                  maxlength="1"
                  v-model="SMSCodeInputs[2]"
                  placeholder="-"
                />
                <input
                  @keyup="focusNextInput(3)"
                  @focus="resetInputValues"
                  id="sms-code-input-3"
                  type="text"
                  maxlength="1"
                  v-model="SMSCodeInputs[3]"
                  placeholder="-"
                />
                <input
                  id="sms-code-input-4"
                  ref="smsCodeInput4"
                  @focus="resetInputValues"
                  type="text"
                  maxlength="1"
                  v-model="SMSCodeInputs[4]"
                  placeholder="-"
                />

                <p @click="setSentToFalseAndClearCode" class="mt-4 text-primary cursor-pointer" data-cy="resend-sms">Send SMS på nytt</p>
              </div>
            </div>

            <div v-else>
              <h2 v-if="isPhoneEditMode" class="mt-4 pt-2">Endre telefonnummer</h2>
              <h2 v-else class="mt-4 pt-2">SMS verifikasjon</h2>

              <p
                v-if="isPhoneEditMode"
                class="mt-4"
              >Hvis du endrer telefonnummeret som skal verifiseres endres også nummeret som er registrert på din bruker.</p>
              <p v-else class="mt-4">
                For å opprette deg som bruker må telefonnummeret ditt verifiseres. Vi sender deg en engangskode på SMS til
                <strong>{{ signup.user.cellPhone }}</strong>
              </p>

              <div v-if="isPhoneEditMode" class="row mt-4 mb-4 text-center">
                <div class="col-12 col-sm-7 mx-auto">
                  <hl-Phone
                    type="text"
                    placeholder="+47"
                    label="Landkode"
                    v-model="signup.user.phonePrefix"
                    name="user.phonePrefix"
                    disabled
                    v-validate
                  >
                    <hl-Input
                      type="tel"
                      label="Mobilnummer"
                      v-model="signup.user.cellPhone"
                      name="user.cellPhone"
                      v-validate
                      data-cy="new-phone-number-component"
                    />
                  </hl-Phone>
                </div>
              </div>


              <p
                v-else
                @click="isPhoneEditMode = true"
                class="text-primary"
                data-cy="change-phone-number"
              >(Klikk her for å endre telefonnummeret)</p>

              <div class="mt-4 pt-2">
                <button 
                  class="btn btn-primary" 
                  :disabled="this._hl_errors.has('user.cellPhone') || isSendingSMS" 
                  data-cy="send-verification-code"
                  @click="sendSMS" 
                >
                  <template v-if="isSendingSMS">sender...</template>
                  <template v-else>send verifikasjonskode</template>
                </button>
                <div v-if="smsError" class="alert alert-danger mt-3" data-cy="sms-error-message">
                  {{ smsError }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <hl-NextBackBottomBar
      buttonSize="lg"
      nextLink="choose"
      nextText="neste"
      backLink="object"
      backText="tilbake"
      :nextDisabled="!readyToGoNext"
    />
  </div>
</template>

<script>
import hlValidateBeforeLeave from '@modules/husleie-framework/src/mixins/hlValidateBeforeLeave';
import husleieapi from '@modules/husleie-api';
import LoaderDots from '@modules/husleie-framework/src/components/loaders/LoaderDots.vue';
import NextBackBottomBar from '@modules/husleie-framework/src/components/navigation/NextBackBottomBar.vue';
import LogOperations from '@modules/husleie-framework/src/static-classes/LogOperations.class.ts';
import Input from '@/components/Input.vue';
import Phone from '@modules/husleie-framework/src/components/forms/inputs/phone/Phone.vue';

const hlApi = new husleieapi();

export default {
  name: 'Utleier',
  mixins: [hlValidateBeforeLeave],
  components: {
    'hl-LoaderDots': LoaderDots,
    'hl-NextBackBottomBar': NextBackBottomBar,
    'hl-Input': Input,
    'hl-Phone': Phone,
  },
  data() {
    return {
      isPhoneEditMode: false,
      isVerificationSuccessful: false,
      isVerificationFailed: false,
      SMSSent: false,
      SMSCodeInputs: ['', '', '', '', ''],
      isSendingSMS: false,
      smsError: undefined,
      readyToGoNext: false,
    };
  },
  props: {
    signup: {
      type: Object,
      required: true,
    },
  },

  computed: {
    verificationCode() {
      let key = '';

      for (let i in this.SMSCodeInputs) {
        key += this.SMSCodeInputs[i];
      }

      // todo, no side effect in computed !!
      this.signup.user.verificationCode = key;
      return key;
    },

     verificationCodeIsNotEmpty() {
        return this.SMSCodeInputs.every((val) => {return val !== ''});
    }
  },

  methods: {
    sendSMS() {
      this.isSendingSMS = true;
      this.smsError = undefined;
      
      let opts = {
        body: {
          cellPhone: this.signup.user.cellPhone
        }
      };

      hlApi.call(
        'SignupApi.verifyCellPhoneSend',
        opts,
        (error, data) => {
          this.isSendingSMS = false;
          
          if (error) {
            LogOperations.error('API|verifyCellPhoneSend', error);
            this.smsError = 'Kunne ikke sende SMS. Prøv igjen senere.';
          } else {
            this.signup.user.verificationToken = data.token;
            this.SMSSent = true;
          }
        }
      );
    },

    verifyCode() {
      let opts = {
        body: {
          cellPhone: this.signup.user.cellPhone,
          token: this.signup.user.verificationToken,
          code: this.verificationCode
        }
      };

      hlApi.call(
        'SignupApi.verifyCellPhoneVerify',
        opts,
        (error, data) => {
          if (error) {
            LogOperations.error('API|verifyCellPhoneVerify', error);
            this.loading = false;
          } else {
            this.loading = false;
            if (data.success) {
              this.isVerificationSuccessful = true;
              this.isVerificationFailed = false;
              this.readyToGoNext = true;
            } else this.isVerificationFailed = true;
          }
        }
      );
    },

    focusNextInput(idx) {
      let getNextInput = JSON.parse(
        JSON.stringify('sms-code-input-' + (idx + 1))
      );

      if (document.getElementById(getNextInput).value === '') {
        document.getElementById(getNextInput).focus();
      }
    },

    resetInputValue(idx) {
      let getInput = JSON.parse(JSON.stringify('sms-code-input-' + idx));

      document.getElementById(getInput).value = '';
    },

    resetInputValues() {
        if (this.verificationCodeIsNotEmpty) {
            this.resetVerificationCode();
            document.getElementById('sms-code-input-0').focus();
        }
    },

    resetVerificationCode() {
      this.SMSCodeInputs = ['', '', '', '', ''];
    },

    setSentToFalseAndClearCode() {
      this.SMSSent = false;
      this.isVerificationSuccessful = false;
      this.isVerificationFailed = false;
      this.resetVerificationCode();
    }
  },

  mounted() {
    /* Tracking GTM */
    /*window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event':'VirtualPageview',
      'virtualPageURL':'/signup/step2/landlordInformation',
      'virtualPageTitle': 'Signup | Step 2 | Landlord Information'
    });*/
  },

  updated() {
    if (this.verificationCode.length > 4) {
      this.verifyCode();
    }
  }
};
</script>

<style scoped lang="scss">
.sms-verification {
  border-radius: $border-radius-medium;
  border: $gray-300;
  box-shadow: $box-shadow-2;
}
.sms-icon {
  font-size: 2rem;
}

.sms-code-inputs {
  input {
    height: 50px;
    width: 50px;
    margin: 0.4rem;
    border-radius: $border-radius;
    background: $white;
    border: 1px solid $gray-300;
    font-size: 24px;
    text-align: center;
    font-weight: 700;
  }
}
</style>
